/**
 * Vault-items die de bot nog moet verwijderen (Jordy, 15-09, POSTING-013 punt 8).
 *
 * Per model te filteren, met een kleine voorbeeldfoto, zodat Jordy zelf op
 * FanCentro kan nakijken of de lijst klopt. Verversen gaat vanzelf: de
 * opruiming haalt een rij pas weg als de vaultfoto aantoonbaar weg is, en dan
 * valt hij hier uit de lijst.
 */
import { useEffect, useState } from 'react'
import { Panel, Table, Tag } from './ui'
import { mediaToken } from '../lib/api'
import { useApi, usePoll } from '../lib/useApi'
import { dt } from '../lib/format'

interface Item {
  post_id: number; model_persona_id: number; model: string | null
  vault_item_id: string; status: string; caption: string | null
  pushed_at: string | null; goes_live_at: string | null; expires_at: string | null
  due: boolean; shared_vault_item: boolean; cleanup_refused: string | null
  thumbnail_url: string
}
interface Antwoord {
  total: number
  models: { model_persona_id: number; model: string | null; count: number }[]
  items: Item[]
}

const KOLOMMEN = [
  { key: 'foto', label: 'Photo' }, { key: 'model', label: 'Model' },
  { key: 'vault', label: 'Vault item' }, { key: 'post', label: 'Post' },
  { key: 'why', label: 'Why it goes' }, { key: 'expires', label: 'Expires' },
  { key: 'note', label: 'Note' },
]

function waarom(it: Item): string {
  if (it.status === 'cancelled') return 'post cancelled'
  if (it.status === 'failed') return 'post failed'
  if (it.due) return 'post expired'
  return 'still live'
}

export function VaultTeVerwijderen() {
  const [model, setModel] = useState('all')
  const [ookLater, setOokLater] = useState(false)
  const [open, setOpen] = useState(false)
  const pad = `/api/admin/posting/vault-to-delete?limit=500&include_upcoming=${ookLater}`
    + (model !== 'all' ? `&model_persona_id=${model}` : '')
  const lijst = useApi<Antwoord>(open ? pad : null, [pad, open])
  usePoll(30000, lijst.refetch, open)
  const [geladen, setGeladen] = useState<Date | null>(null)
  useEffect(() => { if (lijst.data) setGeladen(new Date()) }, [lijst.data])
  const token = encodeURIComponent(mediaToken())
  const data = lijst.data

  return (
    <Panel
      title="Vault items still to delete"
      desc="FanCentro vault photos the bot has not removed yet. An item drops off this list once its photo is proven gone."
      right={
        <button className="btn ghost sm" type="button" onClick={() => setOpen((x) => !x)}>
          {open ? 'Hide list' : 'Show list'}
        </button>
      }
    >
      {!open ? null : (
        <>
          <div className="row" style={{ flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
            <select className="select" value={model} onChange={(e) => setModel(e.target.value)}>
              <option value="all">All models{data ? ` (${data.models.reduce((a, m) => a + m.count, 0)})` : ''}</option>
              {(data?.models ?? []).map((m) => (
                <option key={m.model_persona_id} value={String(m.model_persona_id)}>
                  {`${m.model ?? m.model_persona_id} (${m.count})`}
                </option>
              ))}
            </select>
            <label className="chkrow">
              <span className={ookLater ? 'chk on' : 'chk'} onClick={() => setOokLater((v) => !v)}>
                {ookLater ? '✓' : ''}
              </span>
              Also show photos of posts that are still live
            </label>
            <span className="mut2" style={{ fontSize: 12 }}>
              {data ? `${data.total} item(s)` : 'Loading…'}
              {geladen ? ` · updated ${geladen.toLocaleTimeString('en-GB')} · refreshes every 30 s` : ''}
            </span>
          </div>
          <Table cols={KOLOMMEN} emptyText={data ? 'Nothing left to delete' : 'Loading…'}>
            {(data?.items ?? []).map((it) => (
              <tr key={it.post_id}>
                <td>
                  <a href={`${it.thumbnail_url}?token=${token}`} target="_blank" rel="noreferrer">
                    <img src={`${it.thumbnail_url}?token=${token}`} alt={`post ${it.post_id}`}
                         loading="lazy"
                         style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, background: '#000' }} />
                  </a>
                </td>
                <td><b>{it.model ?? it.model_persona_id}</b></td>
                <td className="mono">{it.vault_item_id}</td>
                <td className="mut" style={{ maxWidth: 280 }}>
                  <span className="mono">#{it.post_id}</span> {it.caption ?? ''}
                </td>
                <td>
                  <Tag kind={it.due ? 'warn' : 'mut'}>{waarom(it)}</Tag>
                </td>
                <td className="mut2 mono" style={{ fontSize: 12 }}>{dt(it.expires_at)}</td>
                <td className="mut2" style={{ fontSize: 12, maxWidth: 240 }}>
                  {it.shared_vault_item ? 'Shared with another post, kept on purpose. ' : ''}
                  {it.cleanup_refused ? `Cleanup refused: ${it.cleanup_refused}` : ''}
                </td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </Panel>
  )
}
